import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import mapValues from 'lodash/mapValues';
import uuid from 'utils/id';

import { ENTITIES_KEY_NODE } from 'common/entities/formNode';
import { INPUT_TYPE_MULTI_SELECT } from 'common/entities/formNode/inputTypes';
import { getFormNode } from 'common/entities/formNode/selectors';

import { getOptionLogicRules } from 'common/entities/formLogicRule/selectors';
import {
    ADD_FORM_NODE_OPTION,
    ADD_FORM_NODE_OPTIONS,
    REMOVE_FORM_NODE_OPTION,
    OVERRIDE_FORM_NODE,
    UPDATE_FORM_NODE_OPTION_RESPONSE_LIMIT,
} from '../constants/actionTypes';
import { INPUT_TYPE, applyCallbackToNodeWithAdditionalGuestsSupport, updateFormNodeConfigValue } from './nodeActions';
import { removeFormLogicEffect } from '../epics/formLogicEffects';
import { updateFormValue } from '../../FormRenderer/actions/form';
import {
    getIsOptionSelectedOrUnset,
    getNodeOptionValueIfSelected,
    getAllNodeOptionsIfSelected,
} from '../utils/nodeOptionsHelpers';

/**
 * @param  {Object} node
 * @param  {string} label
 * @param  {string} [value]
 * @return {Object} - action
 */
export const createActionAddFormNodeOption = (node, label, value = label, optionId = uuid()) => {
    const { entities: prevEntities = {}, result: prevResult = [] } = node.config.options || {};

    const newOption = {
        label,
        value,
        selected: false,
        id: optionId,
    };
    const newNode = {
        ...node,
        config: {
            ...node.config,
            options: {
                result: [...prevResult, optionId],
                entities: {
                    ...prevEntities,
                    [optionId]: newOption,
                },
            },
        },
    };
    return {
        type: ADD_FORM_NODE_OPTION,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]: {
                    [node.id]: newNode,
                },
            },
        },
    };
};

/**
 * Create options from a list of labels
 * @param {Array[string]} optionLabels label list
 */
const createNewOptions = (optionLabels) =>
    optionLabels.reduce(
        (prev, label) => {
            const [ids, newOptions] = prev;

            const id = uuid();
            const newOption = {
                label,
                value: label,
                selected: false,
                id,
            };

            return [[...ids, id], { ...newOptions, [id]: newOption }];
        },
        [[], {}],
    );

/**
 * Create multiple node options for a form node
 * @param {Object} node
 * @param {Array[string]} optionLabels
 */
export const createActionAddMultipleFormNodeOptions = (node, optionLabels) => {
    const { entities: prevEntities = {}, result: prevResult = [] } = node.config.options || {};
    const [newOptionIds, newOptions] = createNewOptions(optionLabels);

    const newNode = {
        ...node,
        config: {
            ...node.config,
            options: {
                result: [...prevResult, ...newOptionIds],
                entities: {
                    ...prevEntities,
                    ...newOptions,
                },
            },
        },
    };
    return {
        type: ADD_FORM_NODE_OPTIONS,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]: {
                    [node.id]: newNode,
                },
            },
        },
    };
};

/**
 * @param  {Object} node
 * @param  {Object} entities
 * @param  {Array} result
 * @param  {String} type
 * @param  {String} entityKey
 * @return {Object} - action
 */
export const createActionUpdateFormNodeOption = (node, entities, result, type, entityKey) => {
    const newNode = {
        ...node,
        config: {
            ...node.config,
            options: {
                result,
                entities,
            },
        },
    };
    return {
        type,
        payload: {
            [entityKey]: {
                [ENTITIES_KEY_NODE]: {
                    [node.id]: newNode,
                },
            },
        },
    };
};

/**
 * @param  {Object} node
 * @param  {string} optionId
 * @param  {number} dropIndex
 * @return {Object} - action
 */
export const createActionMoveFormNodeOption = (node, optionId, dropIndex) => {
    const { entities, result: prevResult = [] } = node.config.options || {};

    let result = [...prevResult];

    result.splice(dropIndex, 0, optionId);
    result = result.filter((oid, index) => !(oid === optionId && index !== dropIndex));

    const newNode = {
        ...node,
        config: {
            ...node.config,
            options: {
                result,
                entities,
            },
        },
    };
    return {
        type: OVERRIDE_FORM_NODE,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]: {
                    [node.id]: newNode,
                },
            },
        },
    };
};

/**
 * @param  {Object} node
 * @param  {string} optionId
 * @param  {bool} selected
 * @return {Object} - action
 */
export const createActionSetFormNodeOptionSelected = (node, optionId, selected, unsetOthers) => {
    const { entities: prevEntities = {}, result = [] } = node.config.options || {};

    const newEntities = mapValues(prevEntities, (option, oid) => ({
        ...option,
        selected: oid === optionId ? selected : getIsOptionSelectedOrUnset(unsetOthers, option.selected),
    }));

    const newNode = {
        ...node,
        config: {
            ...node.config,
            options: {
                result,
                entities: newEntities,
            },
        },
    };
    return {
        type: OVERRIDE_FORM_NODE,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]: {
                    [node.id]: newNode,
                },
            },
        },
    };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Thunks
// ⬇️⬇️⬇️⬇️⬇️⬇️
// /////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Adds an option to a form node
 *
 * @param  {string} nodeId - ID of the form node
 * @param  {string} label - option label
 * @param  {string} [value] - option value
 */
export const addFormNodeOption =
    (nodeId, label, value = label) =>
    (dispatch, getState) => {
        const state = getState();
        const commonOptionId = uuid();

        applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
            (node) => dispatch(createActionAddFormNodeOption(node, label, value)),
            (additionalGuestNode) =>
                dispatch(createActionAddFormNodeOption(additionalGuestNode, label, value, commonOptionId)),
        );
    };

/**
 * Adds an option to a form node
 *
 * @param  {string} nodeId - ID of the form node
 * @param  {Array[string]} optionLabels - option label
 */
export const addMultipleFormNodeOptions = (nodeId, optionLabels) => (dispatch, getState) => {
    const state = getState();
    const commonOptionId = uuid();

    applyCallbackToNodeWithAdditionalGuestsSupport(state, nodeId)(
        (node) => dispatch(createActionAddMultipleFormNodeOptions(node, optionLabels)),
        (additionalGuestNode) =>
            dispatch(createActionAddMultipleFormNodeOptions(additionalGuestNode, optionLabels, commonOptionId)),
    );
};

/**
 * Removes an option to a form node
 *
 * @param  {string} nodeId - ID of the form node
 * @param  {string} optionId - ID of the form node option
 */
export const removeFormNodeOption = (nodeId, optionId) => (dispatch, getState) => {
    const state = getState();
    const node = getFormNode(state, nodeId);

    const { entities: prevEntities = {}, result: prevResult = [] } = node.config.options || {};
    const newResult = prevResult.filter((oid) => oid !== optionId);
    const newEntities = pickBy(prevEntities, (entity, oid) => oid !== optionId);

    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((guestNode) => {
        dispatch(
            createActionUpdateFormNodeOption(
                guestNode,
                newEntities,
                newResult,
                REMOVE_FORM_NODE_OPTION,
                'formBuilderEntities',
            ),
        );

        const logicRulesAffected = getOptionLogicRules(state, guestNode.formId, optionId);

        logicRulesAffected.forEach((rule) => {
            dispatch(removeFormLogicEffect({ logicrulesIds: [rule.id] }));
        });
    });
};

/**
 * Updates an option to a form node
 *
 * @param  {string} nodeId - ID of the form node
 * @param  {string} optionId - ID of the form node option
 * @param  {Object} updates - updated option data
 */
export const updateFormNodeOption = (nodeId, optionId, updates) => (dispatch, getState) => {
    const state = getState();

    const node = getFormNode(state, nodeId);

    const { entities: prevEntities = {}, result = [] } = node.config.options || {};

    const updatedOption = {
        ...prevEntities[optionId],
        ...updates,
        id: optionId,
    };

    const newEntities = {
        ...prevEntities,
        [optionId]: updatedOption,
    };

    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((guestNode) =>
        dispatch(createActionUpdateFormNodeOption(guestNode, newEntities, result, OVERRIDE_FORM_NODE, 'entities')),
    );
};

/**
 * Moves the option given by id to the specified index.
 *
 * @param {string} nodeId
 * @param {string} optionId
 * @param {number} dropIndex
 */
export const moveFormNodeOption = (nodeId, optionId, dropIndex) => (dispatch, getState) => {
    const state = getState();

    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((node) => dispatch(createActionMoveFormNodeOption(node, optionId, dropIndex)));
};

/**
 * Sets the option selected in a form node and formRenderer
 *
 * @param  {string} nodeId - ID of the form node
 * @param  {string} optionId - ID of the form node option
 * @param  {boolean} selected - whether or not the option should be selected
 * @param  {Boolean} unsetOthers - whether to unselect other options
 */
export const setFormNodeOptionSelected = (nodeId, optionId, selected) => (dispatch, getState) => {
    const state = getState();

    const formNode = getFormNode(state, nodeId);
    const unsetOthers = get(formNode, INPUT_TYPE) !== INPUT_TYPE_MULTI_SELECT;

    const newValue = unsetOthers
        ? getNodeOptionValueIfSelected(state, nodeId, optionId, selected)
        : getAllNodeOptionsIfSelected(state, nodeId, optionId, selected);

    // set the selected option in formRenderer
    dispatch(updateFormValue(nodeId, newValue));

    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((additionalGuestNode) => {
        dispatch(createActionSetFormNodeOptionSelected(additionalGuestNode, optionId, selected, unsetOthers));
        // set the selected option in formRenderer for additional guests
        dispatch(updateFormValue(additionalGuestNode.id, newValue));
    });
};

/**
 * @param {string} targetNodeId
 * @param {string} optionId
 * @param {number} responseLimit
 */
export const updateFormNodeOptionResponseLimit = (nodeId, optionId, responseLimit) => (dispatch, getState) => {
    const state = getState();

    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((node) =>
        dispatch({
            type: UPDATE_FORM_NODE_OPTION_RESPONSE_LIMIT,
            nodeId: node.id,
            optionId,
            responseLimit,
        }),
    );
};

export const updateFormNodeConfigChecked = (nodeId, checked) => (dispatch, getState) => {
    const state = getState();

    dispatch(updateFormNodeConfigValue(nodeId, 'checked', checked));
    // set checked on Form Renderer
    dispatch(updateFormValue(nodeId, checked));
    applyCallbackToNodeWithAdditionalGuestsSupport(
        state,
        nodeId,
    )((additionalGuestNode) => {
        dispatch(updateFormNodeConfigValue(additionalGuestNode.id, 'checked', checked));
        dispatch(updateFormValue(additionalGuestNode.id, checked));
    });
};
